'use client';

import React, { useMemo } from 'react';
import NextLink from 'next/link';
import { useParams } from 'next/navigation';
import useClassNames from '@/helpers/hooks/useClassNames';
import { i18nConfig } from '@/project.config';
import { Reference } from '@/types/lib/reference';
import { resolveReference } from '@/utils/lib/resolve-reference';

export type LinkVariant = 'primary' | 'menu-header' | 'menu-item' | 'breadcrumb';

export interface LinkProps extends Omit<React.ComponentProps<typeof NextLink>, 'href' | 'key'> {
  link?: string | Reference;
  variant?: LinkVariant;
  preFetch?: boolean;
  openInNewTab?: boolean;
  underlineOnHover?: boolean;
  chevron?: boolean;
}

type VariantStyle = { [key in LinkVariant]: string };

const variantStyle: VariantStyle = {
  primary: 'text-14 lg:text-16 cursor-pointer text-neutral-500 hover:text-neutral-400',
  'menu-item': 'text-secondary-black hover:underline hover:underline-offset-2',
  breadcrumb: 'text-14 font-medium text-primary-black',
  'menu-header': 'text-14 font-medium text-primary-black cursor-pointer',
};

const Link = ({
  link,
  children,
  className = '',
  variant,
  title = '',
  preFetch = false,
  openInNewTab,
  underlineOnHover,
  chevron,
  ...props
}: React.PropsWithChildren<LinkProps>) => {
  const { locale } = useParams();

  const linkUrl = useMemo(() => {
    if (!link) return '';
    if (typeof link === 'string') return link;

    return resolveReference(link);
  }, [link]);

  // Ensure linkUrl is treated as a string
  const linkUrlString = typeof linkUrl === 'string' ? linkUrl : linkUrl.href || '';

  const linkMissingLocale =
    linkUrlString && linkUrlString.startsWith('/') && i18nConfig.locales.every((locale) => !linkUrlString.startsWith(`/${locale}/`));

  const linkProps = useMemo(() => {
    if (!link || typeof link === 'string') return {};

    const { openInNewTab: _openInNewTab, name: _name, ...rest } = resolveReference(link);
    return rest;
  }, [link]);

  const linkClassNames = useClassNames([variant ? variantStyle[variant] : '', className]);

  return (
    <NextLink
      {...props}
      {...linkProps}
      href={linkUrlString ? (linkMissingLocale ? `/${locale}${linkUrlString}` : linkUrlString) : '#'}
      className={linkClassNames}
      title={title}
      prefetch={preFetch}
      {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </NextLink>
  );
};

export default Link;
